import { useEffect, useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Sparkles, Calendar, AlertTriangle, X, Plus, Stethoscope, Pill, Flower2, Crown, Droplets, Globe2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CLIMATE_ZONE_STYLES } from "@/lib/climate";
import ProUpsell from "@/components/ProUpsell";

const KIND_META = {
  inspection: { Icon: Stethoscope, accent: "text-sky-700", bg: "bg-sky-50", border: "border-sky-200" },
  treatment:  { Icon: Pill,        accent: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200" },
  seasonal:   { Icon: Flower2,     accent: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  queen:      { Icon: Crown,       accent: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  harvest:    { Icon: Droplets,    accent: "text-yellow-700", bg: "bg-yellow-50", border: "border-yellow-200" },
};

const ZONE_STYLES = CLIMATE_ZONE_STYLES;

const PRIORITY_PILL = {
  high:   { label: "Now", classes: "bg-rose-100 text-rose-900 border-rose-200" },
  medium: { label: "Soon", classes: "bg-amber-100 text-amber-900 border-amber-200" },
  low:    { label: "FYI", classes: "bg-stone-100 text-stone-700 border-stone-200" },
};

export default function SmartCalendar() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upsell, setUpsell] = useState(false);
  const [filter, setFilter] = useState("all"); // all | high | medium | low
  const [hiveFilter, setHiveFilter] = useState("all"); // "all" | hive_id
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [pr, zr] = await Promise.all([
        api.get("/predictions"),
        api.get("/predictions/climate-zones"),
      ]);
      setItems(pr.data);
      setZones(zr.data || []);
    } catch (e) {
      if (e.response?.status === 402) setUpsell(true);
      else toast.error(e.response?.data?.detail || "Failed to load suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    setBusy((b) => ({ ...b, [id]: "dismiss" }));
    try {
      await api.post(`/predictions/${id}/dismiss`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Dismissed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  const addReminder = async (id) => {
    setBusy((b) => ({ ...b, [id]: "remind" }));
    try {
      await api.post(`/predictions/${id}/add-reminder`);
      setItems((prev) => prev.map((p) => p.id === id ? { ...p, has_reminder: true } : p));
      toast.success("Added to your reminders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  // Distinct hives appearing in predictions, for the "this hive only" filter row.
  const hiveOptions = useMemo(() => {
    const seen = new Map();
    for (const p of items) {
      if (!seen.has(p.hive_id)) seen.set(p.hive_id, p.hive_name || "Unnamed");
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [items]);

  const counts = useMemo(() => {
    const base = hiveFilter === "all" ? items : items.filter((i) => i.hive_id === hiveFilter);
    return {
      all: base.length,
      high: base.filter((i) => i.priority === "high").length,
      medium: base.filter((i) => i.priority === "medium").length,
      low: base.filter((i) => i.priority === "low").length,
    };
  }, [items, hiveFilter]);

  const filtered = useMemo(() => {
    let base = hiveFilter === "all" ? items : items.filter((i) => i.hive_id === hiveFilter);
    if (filter !== "all") base = base.filter((i) => i.priority === filter);
    return base;
  }, [items, filter, hiveFilter]);

  if (upsell) {
    return (
      <div data-testid="predictions-upsell">
        <ProUpsell open={upsell} onOpenChange={setUpsell} feature="predictions" />
        <div className="text-center py-16 border border-dashed border-stone-300 rounded-2xl max-w-2xl mx-auto">
          <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <p className="text-stone-700 font-medium">Smart Calendar is a Pro feature.</p>
          <p className="text-stone-500 text-sm mt-1">Get predictive treatment, inspection and seasonal nudges tailored to your hives.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="smart-calendar-page">
      <header className="space-y-1">
        <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Pro feature
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Smart calendar</h1>
        <p className="text-stone-600">Predictive nudges based on your hives' location, history and the time of year.</p>
      </header>

      <ClimateZoneSummary zones={zones} />

      {hiveOptions.length > 1 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="smart-calendar-hive-filter">
          <span className="text-xs uppercase tracking-[0.18em] text-stone-500 mr-1">Hive</span>
          <button
            onClick={() => setHiveFilter("all")}
            className={`text-sm px-3 py-1.5 rounded-full border transition ${
              hiveFilter === "all"
                ? "bg-yellow-400 border-yellow-500 text-amber-900 font-semibold"
                : "border-stone-300 hover:border-stone-400 text-stone-700"
            }`}
            data-testid="hive-filter-all"
          >
            All apiary <span className="text-xs opacity-75">({items.length})</span>
          </button>
          {hiveOptions.map((h) => {
            const n = items.filter((i) => i.hive_id === h.id).length;
            const active = hiveFilter === h.id;
            return (
              <button
                key={h.id}
                onClick={() => setHiveFilter(h.id)}
                className={`text-sm px-3 py-1.5 rounded-full border transition ${
                  active
                    ? "bg-yellow-400 border-yellow-500 text-amber-900 font-semibold"
                    : "border-stone-300 hover:border-stone-400 text-stone-700"
                }`}
                data-testid={`hive-filter-${h.id}`}
              >
                {h.name} <span className="text-xs opacity-75">({n})</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="smart-calendar-filters">
        {(["all", "high", "medium", "low"]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-sm px-3 py-1.5 rounded-full border transition ${
              filter === f
                ? "bg-stone-900 border-stone-900 text-white"
                : "border-stone-300 hover:border-stone-400 text-stone-700"
            }`}
            data-testid={`filter-${f}`}
          >
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}{" "}
            <span className="text-xs opacity-75">({counts[f]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-stone-500 py-8">Loading suggestions…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-stone-300 rounded-2xl">
          <Calendar className="w-7 h-7 text-stone-400 mx-auto mb-2" />
          <p className="text-stone-600 text-sm">
            {items.length === 0
              ? "No suggestions yet — add hives and log inspections to get tailored nudges."
              : "Nothing in this priority. Try another filter."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="predictions-list">
          {filtered.map((p) => <Card key={p.id} pred={p} busy={busy[p.id]} onDismiss={dismiss} onRemind={addReminder} />)}
        </ul>
      )}
    </div>
  );
}

function Card({ pred, busy, onDismiss, onRemind }) {
  const meta = KIND_META[pred.kind] || KIND_META.inspection;
  const { Icon, accent, bg, border } = meta;
  const pill = PRIORITY_PILL[pred.priority] || PRIORITY_PILL.low;
  let dateLabel;
  try { dateLabel = format(parseISO(pred.due_date), "MMM d, yyyy"); }
  catch { dateLabel = pred.due_date; }

  return (
    <li
      className={`border ${border} ${bg} rounded-2xl p-4 flex items-start gap-3`}
      data-testid={`prediction-${pred.id}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-white border ${border} shrink-0 ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="font-medium text-stone-900">{pred.title}</div>
          <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full border ${pill.classes}`}>
            {pill.label}
          </span>
        </div>
        <p className="text-sm text-stone-700 mt-1">{pred.reason}</p>
        <div className="text-xs text-stone-500 mt-1.5 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {dateLabel}</span>
          <span className="capitalize">{pred.kind}</span>
          {pred.hive_name && <span>· {pred.hive_name}</span>}
        </div>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        {pred.has_reminder ? (
          <span className="text-[11px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200 inline-flex items-center gap-1" data-testid={`pred-already-reminded-${pred.id}`}>
            <AlertTriangle className="w-3 h-3" /> Reminder set
          </span>
        ) : (
          <Button
            size="sm"
            onClick={() => onRemind(pred.id)}
            disabled={busy === "remind"}
            className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
            data-testid={`pred-add-reminder-${pred.id}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> {busy === "remind" ? "Adding…" : "Add reminder"}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDismiss(pred.id)}
          disabled={busy === "dismiss"}
          className="text-stone-500 hover:text-stone-700"
          data-testid={`pred-dismiss-${pred.id}`}
        >
          <X className="w-3.5 h-3.5 mr-1" /> Dismiss
        </Button>
      </div>
    </li>
  );
}



// ────────── Climate-zone summary block (header explainer) ──────────

function ClimateZoneSummary({ zones }) {
  // Group hives by climate zone so users with 5 hives in 1 zone see one chip, not five.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const z of zones) {
      const key = z.climate_zone;
      if (!map.has(key)) {
        map.set(key, { zone: key, blurb: z.climate_blurb, hives: [] });
      }
      map.get(key).hives.push({ id: z.hive_id, name: z.hive_name, label: z.location_label });
    }
    return [...map.values()];
  }, [zones]);

  if (zones.length === 0) return null;

  const unknown = grouped.find((g) => !g.hives[0]?.label); // hives with no coords default to temperate
  const hasUnknown = zones.some((z) => !z.location_label);

  return (
    <section className="bg-gradient-to-br from-amber-50/60 via-stone-50 to-emerald-50/40 border border-stone-200 rounded-2xl p-5" data-testid="climate-zone-summary">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-white border border-stone-200 grid place-items-center shrink-0">
          <Globe2 className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-lg font-medium">Why these reminders?</h2>
            <span className="text-[10px] uppercase tracking-wider text-stone-400">tailored to your climate</span>
          </div>
          <p className="text-sm text-stone-600 mt-1">
            We adapt inspection cadence, treatment timing and seasonal nudges to each hive's climate zone — derived from its pinned latitude.
          </p>
          <ul className="mt-4 space-y-2.5">
            {grouped.map((g) => {
              const style = ZONE_STYLES[g.zone] || ZONE_STYLES.temperate;
              return (
                <li key={g.zone} className="flex items-start gap-3" data-testid={`climate-zone-${g.zone}`}>
                  <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${style.chip} shrink-0`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} aria-hidden />
                    {style.label}
                  </span>
                  <div className="text-sm text-stone-700 flex-1 min-w-0">
                    <div className="text-stone-600">{g.blurb}</div>
                    <div className="text-xs text-stone-500 mt-0.5 truncate">
                      {g.hives.map((h) => h.name).join(" · ")}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {hasUnknown && unknown == null && (
            <div className="mt-4 flex items-start gap-2 text-xs text-stone-500 bg-white/70 border border-stone-200 rounded-lg p-2.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Some hives don't have a pinned location yet — those default to Temperate. Add a city or pin them on the map for sharper reminders.</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
